import { ContentType } from '@form-factory/schema/enums';
import { Trans } from '@lingui/react/macro';
import { Button, cn, Dialog } from '@utima/ui';
import { File, FileSymlink, Globe } from 'lucide-react';
import { memo, useEffect, useState, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useEnumTranslations } from '@/hooks';
import { trpc } from '@/services/trpc';
import { useBoundStore } from '@/store/store';

import { AddExternalDocument } from './AddExternalDocument';
import { AddWebsites } from './AddWebsites';
import { UploadDocuments } from './UploadDocuments';

export type AddDocumentsModalProps = {
  chatId: string;
  defaultOpen?: boolean;
  defaultUrl?: string;
};

export const AddDocumentsModal = memo(function AddDocumentsModal({
  chatId,
  defaultOpen = false,
  defaultUrl,
}: AddDocumentsModalProps) {
  const enumTranslations = useEnumTranslations();
  const [open, setOpen] = useState(defaultOpen);
  const { type, setType } = useBoundStore(
    useShallow(state => ({
      type: state.type,
      setType: state.setType,
    })),
  );

  const [selectedChatIds, setSelectedChatIds] = useState<string[]>([chatId]);
  const [pendingAddCount, setPendingAddCount] = useState(0);

  // Fetch Chat Data with specific document counts
  const { data: chatData, isLoading: isLoadingChat } =
    trpc.chats.findOne.useQuery({
      id: chatId,
      populate: ['documentsCount', 'organization.tierConfig'],
    });
  const organizationId = chatData?.organization?.id;

  // Fetch Subscription Data
  const { data: subscriptionData, isLoading: isLoadingSub } =
    trpc.subscriptions.getActive.useQuery(
      { organizationId: organizationId! },
      { enabled: !!organizationId },
    );

  // Calculate limit status using populated counts
  const totalDocs = Number(chatData?.documentsCount ?? 0);
  const totalProjectedCount = totalDocs + pendingAddCount;

  // Treat maxDocuments === 0 as Infinity (unlimited)
  const limit =
    subscriptionData?.maxDocuments === 0
      ? Infinity
      : (subscriptionData?.maxDocuments ?? Infinity);
  const limitReached = totalDocs > limit;
  const projectedLimitReached = totalProjectedCount > limit;

  const websiteButtonRef = useRef<HTMLButtonElement>(null);
  const externalDocButtonRef = useRef<HTMLButtonElement>(null);
  const documentButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (defaultUrl) {
      setType(ContentType.Website);
    }
  }, [defaultUrl, setType, type]);

  const handleOpenAutoFocus = (event: Event) => {
    event.preventDefault();
    switch (type) {
      case 'website':
        websiteButtonRef.current?.focus();
        break;
      case 'externalDocument':
        externalDocButtonRef.current?.focus();
        break;
      case 'document':
        documentButtonRef.current?.focus();
        break;
    }
  };

  useEffect(() => {
    if (defaultUrl) {
      setType(ContentType.Website);
    }
  }, [defaultUrl, setType]);

  return (
    <Dialog.Root
      defaultOpen={defaultOpen}
      open={open}
      onOpenChange={isOpen => {
        setOpen(isOpen);

        if (isOpen) {
          setSelectedChatIds([chatId]);
        }
      }}
    >
      <Dialog.Trigger asChild>
        <Button data-cta='admin-add-documents-modal-trigger'>
          <Trans>Add resources</Trans>
        </Button>
      </Dialog.Trigger>
      <Dialog.Content
        className='flex max-h-full min-h-[400px] w-[1000px] max-w-full flex-col overflow-auto'
        onOpenAutoFocus={handleOpenAutoFocus}
      >
        <Dialog.Header>
          <Dialog.Title>
            <Trans>Add resources</Trans>
          </Dialog.Title>
          <Dialog.Description>
            <Trans>
              Below you can upload your own documents, or add websites as
              sources.
            </Trans>
          </Dialog.Description>
        </Dialog.Header>

        {/* Display Limit Info */}
        <div
          className={cn(
            'text-sm font-medium',
            projectedLimitReached && limit !== Infinity
              ? 'text-red-600 font-medium'
              : 'text-gray-600',
          )}
        >
          {isLoadingChat || isLoadingSub ? (
            <Trans>Loading document limit...</Trans>
          ) : projectedLimitReached && limit !== Infinity ? (
            <Trans>
              {totalProjectedCount} / {limit} documents uploaded (Limit Reached)
            </Trans>
          ) : (
            <Trans>
              Documents: {totalProjectedCount} /{' '}
              {limit === Infinity ? 'Unlimited' : limit}
            </Trans>
          )}
        </div>

        <div className='grid grid-cols-3 gap-2 md:gap-4'>
          <Button
            data-cta='admin-add-documents-modal-website-button'
            ref={websiteButtonRef}
            variant='ghost'
            outline
            className={cn(
              'py-4 flex grow flex-row items-center gap-2',
              type === 'website' && 'bg-gray-100 text-primary',
            )}
            onClick={() => setType(ContentType.Website)}
          >
            <Globe className='size-5' />
            <span className='hidden sm:inline'>
              {enumTranslations.contentType[ContentType.Website]}
            </span>
          </Button>
          <Button
            data-cta='admin-add-documents-modal-external-document-button'
            ref={externalDocButtonRef}
            variant='ghost'
            outline
            className={cn(
              'py-4 flex grow flex-row items-center gap-2',
              type === 'externalDocument' && 'bg-gray-100 text-primary',
            )}
            onClick={() => setType(ContentType.ExternalDocument)}
          >
            <FileSymlink className='size-5' />
            <span className='hidden sm:inline'>
              {enumTranslations.contentType[ContentType.ExternalDocument]}
            </span>
          </Button>
          <Button
            data-cta='admin-add-documents-modal-document-button'
            ref={documentButtonRef}
            variant='ghost'
            outline
            className={cn(
              'py-4 flex grow flex-row items-center gap-2',
              type === 'document' && 'bg-gray-100 text-primary',
            )}
            onClick={() => setType(ContentType.Document)}
          >
            <File className='size-5' />
            <span className='hidden sm:inline'>
              {enumTranslations.contentType[ContentType.Document]}
            </span>
          </Button>
        </div>

        {type === 'website' && (
          <AddWebsites
            chatIds={selectedChatIds}
            setOpen={setOpen}
            defaultUrl={defaultUrl}
            limitReached={limitReached}
            projectedLimitReached={projectedLimitReached}
            onPendingCountChange={setPendingAddCount}
          />
        )}
        {type === 'externalDocument' && (
          <AddExternalDocument
            chatIds={selectedChatIds}
            setOpen={setOpen}
            limitReached={limitReached}
            projectedLimitReached={projectedLimitReached}
            onPendingCountChange={setPendingAddCount}
          />
        )}
        {type === 'document' && (
          <UploadDocuments
            chatIds={selectedChatIds}
            limitReached={limitReached}
            projectedLimitReached={projectedLimitReached}
          />
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
});
